"use client";

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import AdminStats from './components/AdminStats';
import AdminCharts from './components/AdminCharts';
import GossipTable, { INITIAL_GOSSIP_ROWS } from './components/GossipTable';
import LiveQueue from './components/LiveQueue';
import GossipDetailDrawer from './components/GossipDetailDrawer';
import type { GossipRow, GossipStatus, GossipComment } from './components/GossipDetailDrawer';

export default function HostAdminPanelPage() {
  const [rows, setRows] = useState<GossipRow[]>(INITIAL_GOSSIP_ROWS);
  const [selectedGossip, setSelectedGossip] = useState<GossipRow | null>(null);

  const handleStatusChange = (id: string, newStatus: GossipStatus) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r))
    );
    setSelectedGossip((prev) =>
      prev?.id === id ? { ...prev, status: newStatus } : prev
    );
  };

  const handleDelete = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    if (selectedGossip?.id === id) setSelectedGossip(null);
  };

  const handleAddComment = (gossipId: string, text: string) => {
    const newComment: GossipComment = {
      id: `c-${Date.now()}`,
      text,
      timestamp: new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }),
    };
    setRows((prev) =>
      prev.map((r) =>
        r.id === gossipId
          ? { ...r, comments: [...(r.comments ?? []), newComment] }
          : r
      )
    );
    setSelectedGossip((prev) =>
      prev?.id === gossipId
        ? { ...prev, comments: [...(prev.comments ?? []), newComment] }
        : prev
    );
  };

  const handleDeleteComment = (gossipId: string, commentId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === gossipId
          ? { ...r, comments: (r.comments ?? []).filter((c) => c.id !== commentId) }
          : r
      )
    );
    setSelectedGossip((prev) =>
      prev?.id === gossipId
        ? { ...prev, comments: (prev.comments ?? []).filter((c) => c.id !== commentId) }
        : prev
    );
  };

  const handleRemoveFromQueue = (id: string) => {
    handleStatusChange(id, 'pendiente');
  };

  return (
    <AppLayout>
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Panel de la Anfitriona 🧶</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Gestiona los chismes, controla la cola y monitorea el live.
            </p>
          </div>
          <div className="flex items-center gap-2 bg-red-100 text-red-700 border border-red-200 rounded-xl px-4 py-2.5 text-sm font-semibold live-pulse">
            <span className="w-2 h-2 bg-red-500 rounded-full dot-pulse" />
            Transmitiendo en vivo
          </div>
        </div>

        <AdminStats />
        <AdminCharts />

        <div className="mb-6">
          <LiveQueue
            items={rows}
            onStatusChange={handleStatusChange}
            onRemoveFromQueue={handleRemoveFromQueue}
            onOpenDetail={setSelectedGossip}
          />
        </div>

        <GossipTable
          rows={rows}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          onOpenDetail={setSelectedGossip}
        />
      </div>

      <GossipDetailDrawer
        gossip={selectedGossip}
        onClose={() => setSelectedGossip(null)}
        onStatusChange={handleStatusChange}
        onAddComment={handleAddComment}
        onDeleteComment={handleDeleteComment}
      />
    </AppLayout>
  );
}